import type { ReportTypeId } from '../../config/reportTypes';
import { ECONOMIC_SOURCE_CATALOG } from '../../data/economicContribution/sourceCatalog';
import { WATER_USE_BENCHMARK_CATALOG } from '../../data/economicContribution/waterUseBenchmarks';
import type {
  ComparableObservation,
  EconomicContributionScreeningSnapshot,
  EconomicProfileSnapshot,
  EconomicSourceRef,
  WaterUseScenarioResult,
  WaterUseScreeningResult,
} from '../../types/economicContribution';
import type {
  EconomicResearchComparableSummary,
  EconomicResearchEvidenceStep,
  EconomicResearchFacet,
  EconomicResearchRun,
  EconomicResearchSitePointer,
} from '../../types/economicResearch';
import type { SiteEntity } from '../../types/site';
import type { RefdataFipsResult } from '../../services/refdata/fipsLookup';
import {
  SED_ECONOMIC_EVIDENCE_SCHEMA_VERSION,
  type EconomicEvidenceData,
  type EvidenceSourceContribution,
  type EvidenceSection,
  type SedEconomicResearchEvidence,
} from '../../types/siteEvidence';
import { WATER_SCREENING_VIEW_SCHEMA_VERSION, type WaterScreeningView } from '../../types/siteRag';
import { hashEconomicModelInput } from './modelUtils';
import { economicScreenRequestFromSite } from './siteScreeningInput';
import { isCurrentWaterUseScreeningResult } from './waterUseScreening';
import { formatAnnualWaterBand, formatHourlyWaterBand, formatWaterRateBand } from '../waterRateFormat';
import {
  capitalizeFirst,
  countWord,
  formatCelsius,
  formatCelsiusRange,
  formatDerivedMwRange,
  formatFractionPercentRange,
  formatPueRange,
  formatRecordedMwScope,
  formatWholeNumber,
  formatWholeRange,
  joinWithAnd,
} from '../water/waterScreeningFormat';
import {
  DATA_CENTER_OPERATING_MODEL_LABELS,
  DATA_CENTER_WORKLOAD_LABELS,
  DRY_COOLING_CAPACITY_STATUS_LABELS,
  HEAT_REJECTION_LABELS,
  IT_COOLING_LOOP_LABELS,
  POWER_MW_BASIS_LABELS,
  POWER_MW_BASIS_TREATMENT_LABELS,
  SCREENING_INPUT_SOURCE_LABELS,
  TMY_SOURCE_LABELS,
  WATER_APPLICABILITY_LABELS,
  WATER_BOUNDARY_LABELS,
  WATER_FLOW_BASIS_LABELS,
  WATER_SCENARIO_TECHNOLOGY_LABELS,
  WET_BULB_BASIS_LABELS,
} from '../water/waterScreeningLabels';

export const ECONOMIC_EVIDENCE_SOURCE_REVISION_NONE = 'economic-evidence:none' as const;

type EconomicSiteSnapshots = Pick<
  SiteEntity,
  | 'id'
  | 'location'
  | 'projectIntent'
  | 'economicContributionInputs'
  | 'economicProfileSnapshot'
  | 'economicContributionSnapshot'
  | 'economicResearchSnapshotPointer'
>;

/** Stable revision over the server-authored snapshot identities on a site. */
export function economicEvidenceRevisionForSite(site: EconomicSiteSnapshots): string {
  const profileId = site.economicProfileSnapshot?.snapshotId ?? null;
  const screeningId = site.economicContributionSnapshot?.snapshotId ?? null;
  const researchId = site.economicResearchSnapshotPointer?.resultSnapshotId ?? null;
  if (!profileId && !screeningId && !researchId) return ECONOMIC_EVIDENCE_SOURCE_REVISION_NONE;
  return `economic-evidence:${hashEconomicModelInput({
    contract: 'sed-economic-evidence-source-revision-v1',
    siteId: site.id,
    location: site.location,
    screeningInput: economicScreenRequestFromSite(site as SiteEntity),
    waterBenchmarkCatalogVersion: WATER_USE_BENCHMARK_CATALOG.catalogVersion,
    profileId,
    screeningId,
    researchId,
  })}`;
}

/** Geography guard for a persisted workforce profile. */
export function economicProfileMatchesFips(
  profile: EconomicProfileSnapshot | null | undefined,
  fips: RefdataFipsResult | null | undefined,
): boolean {
  return Boolean(
    profile
    && fips
    && profile.schemaVersion === 1
    && profile.countyFips === fips.countyFips
    && profile.stateFips === fips.stateFips
    && profile.stateCode === fips.stateCode
    && profile.snapshotId.trim(),
  );
}

/** Recompute the exact screen input hash from current site intent + FIPS. */
export function economicScreeningMatchesSite(
  site: SiteEntity,
  screening: EconomicContributionScreeningSnapshot | null | undefined,
  fips: RefdataFipsResult | null | undefined,
): boolean {
  if (!screening || !fips || screening.siteId !== site.id) return false;
  const request = economicScreenRequestFromSite(site);
  if (!request) return false;
  const locatedRequest = {
    ...request,
    directJobs: {
      ...request.directJobs,
      location: {
        stateFips: fips.stateFips,
        countyFips: fips.countyFips,
        stateCode: fips.stateCode,
        countyName: fips.countyName,
      },
    },
  };
  return screening.location.stateFips === fips.stateFips
    && screening.location.countyFips === fips.countyFips
    && screening.location.stateCode === fips.stateCode
    && screening.sourceCatalogVersion === ECONOMIC_SOURCE_CATALOG.catalogVersion
    && (locatedRequest.waterUse == null || isCurrentWaterUseScreeningResult(
      screening.waterUseScreening,
      { requireTmyAttempt: locatedRequest.waterUse.siteCoordinates != null },
    ))
    && screening.inputHash === hashEconomicModelInput(locatedRequest);
}

export function economicResearchRunMatchesSitePointer(
  site: SiteEntity,
  run: EconomicResearchRun | null | undefined,
): boolean {
  const pointer = site.economicResearchSnapshotPointer;
  const result = run?.resultSnapshot;
  return Boolean(
    pointer
    && run
    && result
    && run.stage === 'complete'
    && run.terminalResult !== 'failed'
    && run.siteId === site.id
    && run.runId === pointer.runId
    && run.siteFingerprint === pointer.siteFingerprint
    && result.snapshotId === pointer.resultSnapshotId
    && result.siteFingerprint === pointer.siteFingerprint,
  );
}

export function economicResearchSitePointerForRun(
  run: EconomicResearchRun,
): EconomicResearchSitePointer | null {
  const result = run.resultSnapshot;
  if (run.stage !== 'complete' || run.terminalResult === 'failed' || !result) return null;
  return {
    schemaVersion: 1,
    runId: run.runId,
    resultSnapshotId: result.snapshotId,
    siteFingerprint: result.siteFingerprint,
    generatedAt: result.generatedAt,
    policyVersion: run.policyVersion,
    sourceDefinitionVersion: run.sourceDefinitionVersion,
  };
}

function screeningSourceIds(screening: EconomicContributionScreeningSnapshot | undefined): Set<string> {
  if (!screening) return new Set();
  const ids = new Set<string>(screening.directJobs.sourceEntryIds);
  for (const id of screening.directJobsMwRange?.sourceEntryIds ?? []) ids.add(id);
  for (const id of screening.constructionJobs?.sourceEntryIds ?? []) ids.add(id);
  for (const id of screening.constructionJobsMwRange?.sourceEntryIds ?? []) ids.add(id);
  for (const item of screening.additionalScenarios ?? []) {
    for (const id of item.directJobs.sourceEntryIds ?? []) ids.add(id);
    for (const id of item.directJobsMwRange?.sourceEntryIds ?? []) ids.add(id);
    for (const id of item.constructionJobs?.sourceEntryIds ?? []) ids.add(id);
    for (const id of item.constructionJobsMwRange?.sourceEntryIds ?? []) ids.add(id);
  }
  return ids;
}

function resolveScreeningSources(
  screening: EconomicContributionScreeningSnapshot | undefined,
): EconomicSourceRef[] {
  const ids = screeningSourceIds(screening);
  const byId = new Map<string, EconomicSourceRef>();
  for (const entry of ECONOMIC_SOURCE_CATALOG.entries) {
    if (!ids.has(entry.id)) continue;
    for (const source of entry.sources) byId.set(source.id, source);
  }
  return [...byId.values()].sort((left, right) => left.id.localeCompare(right.id));
}

function resolveWaterScreeningSources(
  screening: EconomicContributionScreeningSnapshot | undefined,
): EconomicSourceRef[] {
  const byId = new Map<string, EconomicSourceRef>();
  const screens = [screening?.waterUseScreening, ...(screening?.additionalScenarios ?? []).map(item => item.waterUseScreening)];
  for (const screen of screens) for (const scenario of screen?.scenarios ?? []) {
    for (const source of scenario.sourceRefs) byId.set(`${source.id}|${source.url}`, source);
  }
  return [...byId.values()].sort((left, right) => left.id.localeCompare(right.id));
}

function economicSourceIdentity(source: Pick<EconomicSourceRef, 'url' | 'locator' | 'excerpt'>): string {
  return JSON.stringify([source.url, source.locator, source.excerpt]);
}

function researchSources(research: SedEconomicResearchEvidence | undefined): EconomicSourceRef[] {
  if (!research) return [];
  const byKey = new Map<string, EconomicSourceRef>();
  const add = (source: EconomicSourceRef): void => {
    byKey.set(economicSourceIdentity(source), source);
  };
  for (const record of research.policyValidRecords) {
    for (const source of record.mw?.sources ?? []) add(source);
    for (const source of record.permanentJobs?.sources ?? []) add(source);
    for (const source of observationSources(record.waterTimeline as readonly ComparableObservation<unknown>[] | undefined)) add(source);
    for (const source of observationSources(record.coolingObservations as readonly ComparableObservation<unknown>[] | undefined)) add(source);
    for (const source of observationSources(record.powerObservations as readonly ComparableObservation<unknown>[] | undefined)) add(source);
    for (const source of observationSources(record.deliveryEvents as readonly ComparableObservation<unknown>[] | undefined)) add(source);
  }
  return [...byKey.values()].sort((left, right) => left.id.localeCompare(right.id));
}

function workforceSourceContributions(
  profile: EconomicProfileSnapshot | undefined,
  fallbackRetrievedAt: string,
): EvidenceSourceContribution[] {
  const byUrl = new Map<string, { label: string; period: string }>();
  for (const observation of profile?.observations ?? []) {
    byUrl.set(observation.sourceUrl, {
      label: `${observation.sourceDataset} (${observation.geography.label})`,
      period: observation.period,
    });
  }
  return [...byUrl.entries()].map(([sourceUrl, source]) => ({
    path: 'workforceProfile.observations',
    grade: 'authoritative-government',
    qualification: 'screening',
    source: source.label,
    sourceUrl,
    retrievedAt: profile?.generatedAt ?? fallbackRetrievedAt,
    dataAsOf: source.period,
  }));
}

function economicSourceContributions(
  sources: readonly EconomicSourceRef[],
  options: Pick<EvidenceSourceContribution, 'path' | 'grade' | 'qualification' | 'retrievedAt'>,
): EvidenceSourceContribution[] {
  const byIdentity = new Map<string, EvidenceSourceContribution>();
  for (const source of sources) {
    byIdentity.set(economicSourceIdentity(source), {
      ...options,
      source: `${source.publisher}: ${source.title}`,
      sourceUrl: source.url,
      dataAsOf: source.publicationDate,
      excerptPreview: source.excerpt.slice(0, 140),
    });
  }
  return [...byIdentity.values()];
}

function publishedResearch(run: EconomicResearchRun): SedEconomicResearchEvidence {
  const result = run.resultSnapshot!;
  return {
    runId: run.runId,
    resultSnapshotId: result.snapshotId,
    siteFingerprint: result.siteFingerprint,
    generatedAt: result.generatedAt,
    policyVersion: run.policyVersion,
    sourceDefinitionVersion: run.sourceDefinitionVersion,
    ...(result.objective ? { objective: result.objective } : {}),
    ...(result.facetCoverage ? { facetCoverage: result.facetCoverage } : {}),
    policyValidRecords: result.policyValidRecords,
    ...(result.comparableAggregates ? { comparableAggregates: result.comparableAggregates } : {}),
    sourceFailures: result.sourceFailures ?? [],
    basisCounts: result.basisCounts,
    fiscalBaselineDrafts: result.fiscalBaselineDrafts,
    nonPromoted: {
      heldCount: result.held.length,
      excludedCount: result.excluded.length,
    },
  };
}

function hasJobsOrFiscalScreeningFact(screening: EconomicContributionScreeningSnapshot | undefined): boolean {
  if (!screening) return false;
  return screening.directJobs.status !== 'not-calculated'
    || (screening.directJobsMwRange != null
      && screening.directJobsMwRange.status !== 'not-calculated')
    || (screening.constructionJobs != null && screening.constructionJobs.status !== 'not-calculated')
    || (screening.constructionJobsMwRange != null
      && screening.constructionJobsMwRange.status !== 'not-calculated')
    || screening.fiscalScenarios.length > 0
    || screening.comparableMatches.length > 0;
}

function hasScreeningFact(screening: EconomicContributionScreeningSnapshot | undefined): boolean {
  return hasJobsOrFiscalScreeningFact(screening)
    || screening?.waterUseScreening?.status === 'calculated';
}

function latestIso(values: readonly (string | undefined)[], fallback: string): string {
  return values.filter((value): value is string => Boolean(value)).sort().pop() ?? fallback;
}

/**
 * Freeze the canonical economic snapshots plus the current policy-valid
 * research result into one SED section. Raw artifacts/claims remain in their
 * immutable stores and are reachable through the record source trails.
 */
export function buildEconomicEvidenceSection(
  site: SiteEntity,
  run: EconomicResearchRun | null | undefined,
  capturedAt = new Date().toISOString(),
): EvidenceSection<EconomicEvidenceData> {
  const workforceProfile = site.economicProfileSnapshot;
  const screening = site.economicContributionSnapshot;
  const research = economicResearchRunMatchesSitePointer(site, run) ? publishedResearch(run!) : undefined;
  const screeningSources = resolveScreeningSources(screening);
  const waterUseScreening = screening?.waterUseScreening?.status === 'calculated'
    ? screening.waterUseScreening
    : undefined;
  const waterScreeningSources = resolveWaterScreeningSources(screening);
  const comparableSources = researchSources(research);
  const sourceRevision = economicEvidenceRevisionForSite(site);
  const payload = {
    schemaVersion: SED_ECONOMIC_EVIDENCE_SCHEMA_VERSION,
    sourceRevision,
    ...(workforceProfile ? { workforceProfile } : {}),
    ...(waterUseScreening ? { waterUseScreening } : {}),
    ...(screening?.additionalScenarios?.length ? {
      waterUseScenarioScreens: screening.additionalScenarios.flatMap(({ scenarioId, label, assumption, waterUseScreening: result }) =>
        result?.status === 'calculated' ? [{ scenarioId, label, assumption, result }] : []),
    } : {}),
    waterScreeningSources,
    // Water is a separately projected evidence facet. Keep the jobs/fiscal
    // screen structurally independent so a water-only report cannot inherit
    // unrelated employment claims through this nested snapshot.
    ...(screening ? { screening: {
      ...screening,
      waterUseScreening: undefined,
      ...(screening.additionalScenarios ? {
        additionalScenarios: screening.additionalScenarios.map(({ waterUseScreening: _water, ...jobs }) => jobs),
      } : {}),
    } } : {}),
    screeningSources,
    ...(research ? { research } : {}),
  };
  const contentHash = hashEconomicModelInput({
    contract: 'sed-economic-evidence-content-v1',
    payload,
  });
  const data: EconomicEvidenceData = { ...payload, contentHash, capturedAt };
  const hasFacts = (workforceProfile?.observations.length ?? 0) > 0
    || hasScreeningFact(screening)
    || (research?.policyValidRecords.length ?? 0) > 0;
  const retrievedAt = latestIso(
    [workforceProfile?.generatedAt, screening?.generatedAt, research?.generatedAt],
    capturedAt,
  );
  const sourceContributions = [
    ...workforceSourceContributions(workforceProfile, retrievedAt),
    ...economicSourceContributions(screeningSources, {
      path: 'screening',
      grade: 'inference',
      qualification: 'estimated',
      retrievedAt: screening?.generatedAt ?? retrievedAt,
    }),
    ...economicSourceContributions(waterScreeningSources, {
      path: 'waterUseScreening',
      grade: 'inference',
      qualification: 'estimated',
      retrievedAt: screening?.generatedAt ?? retrievedAt,
    }),
    ...economicSourceContributions(comparableSources, {
      path: 'research.policyValidRecords',
      grade: 'regional-screening',
      qualification: 'screening',
      retrievedAt: research?.generatedAt ?? retrievedAt,
    }),
  ];
  const anomalies = [
    ...(research?.sourceFailures.map(failure => (
      `Research source unavailable: ${failure.sourceClass} (${failure.failureKind ?? 'other'})`
    )) ?? []),
    ...(research && (research.nonPromoted.heldCount > 0 || research.nonPromoted.excludedCount > 0)
      ? [`${research.nonPromoted.heldCount} held and ${research.nonPromoted.excludedCount} excluded research candidates were not promoted as facts.`]
      : []),
  ];
  return {
    status: hasFacts ? 'verified' : 'unavailable',
    data,
    source: 'Federal workforce and water benchmark datasets + versioned economic screening + policy-valid public-record comparable research',
    retrievedAt,
    sourceGrade: hasFacts ? 'regional-screening' : 'inference',
    qualification: hasFacts ? 'screening' : 'unavailable',
    sourceContributions,
    confidence: hasFacts ? 0.8 : 0,
    anomalies,
    rawResponseHash: contentHash,
  };
}

const ECONOMIC_FACETS_BY_REPORT: Partial<Record<ReportTypeId, readonly EconomicResearchFacet[]>> = {
  feasibility: ['jobs', 'water', 'cooling', 'power', 'delivery'],
  environmental: ['jobs', 'water', 'cooling', 'delivery'],
  // P3.2 — the water duty table now rides on Generation & Impact, so the
  // water prompt policy and WATER-TRUTH-1 apply to it as well.
  'power-availability': ['power', 'cooling', 'delivery', 'water'],
  'water-resource': ['water', 'cooling', 'delivery'],
  'supply-chain-risk': ['cooling', 'power', 'delivery'],
  'public-sentiment': ['jobs', 'water', 'cooling', 'power', 'delivery'],
  'custom-brief': ['jobs', 'water', 'cooling', 'power', 'delivery'],
  'executive-summary': ['jobs', 'water', 'cooling', 'power', 'delivery'],
  financial: ['jobs', 'power', 'delivery'],
};

export function economicEvidenceFacetsForReport(
  reportType: ReportTypeId,
): readonly EconomicResearchFacet[] {
  return ECONOMIC_FACETS_BY_REPORT[reportType] ?? [];
}

/**
 * Runtime-safe facet check for trust gates that receive a report type from a
 * persisted or Agent Platform payload rather than the compile-time registry.
 *
 * Keep this coupled to `ECONOMIC_FACETS_BY_REPORT`: if a new report begins
 * receiving economic water evidence, WATER-TRUTH-1 must protect it without a
 * second allow-list drifting out of date.
 */
export function reportProjectsEconomicFacet(
  reportType: string,
  facet: EconomicResearchFacet,
): boolean {
  return (ECONOMIC_FACETS_BY_REPORT[reportType as ReportTypeId] ?? []).includes(facet);
}

function observationSources(observations: readonly ComparableObservation<unknown>[] | undefined): EconomicSourceRef[] {
  return (observations ?? []).flatMap(observation => [...observation.sources]);
}

function allComparableSources(record: EconomicResearchComparableSummary): EconomicSourceRef[] {
  return [
    ...(record.mw?.sources ?? []),
    ...(record.permanentJobs?.sources ?? []),
    ...observationSources(record.waterTimeline as readonly ComparableObservation<unknown>[] | undefined),
    ...observationSources(record.coolingObservations as readonly ComparableObservation<unknown>[] | undefined),
    ...observationSources(record.powerObservations as readonly ComparableObservation<unknown>[] | undefined),
    ...observationSources(record.deliveryEvents as readonly ComparableObservation<unknown>[] | undefined),
  ];
}

function evidenceStepMatchesSelectedSources(
  step: EconomicResearchEvidenceStep,
  selectedSources: readonly EconomicSourceRef[],
  allSources: readonly EconomicSourceRef[],
): boolean {
  if (!step.sourceUrl) return false;
  const selectedForUrl = selectedSources.filter(source => source.url === step.sourceUrl);
  if (selectedForUrl.length === 0) return false;
  if (step.locator != null || step.excerpt != null) {
    return selectedForUrl.some(source => (
      (step.locator == null || step.locator === source.locator)
      && (step.excerpt == null || step.excerpt === source.excerpt)
    ));
  }

  // Legacy evidence steps may only carry a URL. Preserve that fallback only
  // when every source ref for the document survived facet projection; a shared
  // PDF with selected and excluded excerpts is otherwise ambiguous.
  const selectedIdentities = new Set(selectedForUrl.map(economicSourceIdentity));
  return allSources
    .filter(source => source.url === step.sourceUrl)
    .every(source => selectedIdentities.has(economicSourceIdentity(source)));
}

function filterComparableForFacets(
  record: EconomicResearchComparableSummary,
  facets: ReadonlySet<EconomicResearchFacet>,
): EconomicResearchComparableSummary | null {
  const jobs = facets.has('jobs');
  const waterTimeline = facets.has('water') ? record.waterTimeline : undefined;
  const coolingObservations = facets.has('cooling') ? record.coolingObservations : undefined;
  const powerObservations = facets.has('power') ? record.powerObservations : undefined;
  const deliveryEvents = facets.has('delivery') ? record.deliveryEvents : undefined;
  const hasSelected = jobs && (record.mw != null || record.permanentJobs != null)
    || (waterTimeline?.length ?? 0) > 0
    || (coolingObservations?.length ?? 0) > 0
    || (powerObservations?.length ?? 0) > 0
    || (deliveryEvents?.length ?? 0) > 0;
  if (!hasSelected) return null;
  const sources = [
    ...(jobs ? [...(record.mw?.sources ?? []), ...(record.permanentJobs?.sources ?? [])] : []),
    ...observationSources(waterTimeline as readonly ComparableObservation<unknown>[] | undefined),
    ...observationSources(coolingObservations as readonly ComparableObservation<unknown>[] | undefined),
    ...observationSources(powerObservations as readonly ComparableObservation<unknown>[] | undefined),
    ...observationSources(deliveryEvents as readonly ComparableObservation<unknown>[] | undefined),
  ];
  const allSources = allComparableSources(record);
  return {
    projectId: record.projectId,
    facilityName: record.facilityName,
    locality: record.locality,
    stateCode: record.stateCode,
    score: record.score,
    ...(jobs && record.mw ? { mw: record.mw } : {}),
    ...(jobs && record.permanentJobs ? { permanentJobs: record.permanentJobs } : {}),
    reasons: jobs ? record.reasons : [],
    incompatibilities: jobs ? record.incompatibilities : [],
    compatibleForJobsPerMw: jobs ? record.compatibleForJobsPerMw : false,
    evidenceTrail: record.evidenceTrail.filter(step => (
      (jobs && step.kind === 'compatibility-decision')
      || evidenceStepMatchesSelectedSources(step, sources, allSources)
    )),
    ...(waterTimeline?.length ? { waterTimeline } : {}),
    ...(coolingObservations?.length ? { coolingObservations } : {}),
    ...(powerObservations?.length ? { powerObservations } : {}),
    ...(deliveryEvents?.length ? { deliveryEvents } : {}),
  };
}

/** Report-type projection: retain only evidence facets this report consumes. */
export function projectEconomicEvidenceForReport(
  section: EvidenceSection<EconomicEvidenceData>,
  reportType: ReportTypeId,
): EvidenceSection<EconomicEvidenceData> | undefined {
  const facets = new Set(economicEvidenceFacetsForReport(reportType));
  if (facets.size === 0) return undefined;
  const data = section.data;
  if (!data) return undefined;
  const jobsContext = facets.has('jobs');
  const waterContext = facets.has('water');
  const research = data.research;
  const projectedResearch = research ? {
    ...research,
    objective: research.objective ? {
      ...research.objective,
      requestedFacets: research.objective.requestedFacets.filter(facet => facets.has(facet)),
    } : undefined,
    facetCoverage: research.facetCoverage
      ? Object.fromEntries(Object.entries(research.facetCoverage).filter(([facet]) => (
          facets.has(facet as EconomicResearchFacet)
        )))
      : undefined,
    policyValidRecords: research.policyValidRecords
      .map(record => filterComparableForFacets(record, facets))
      .filter((record): record is EconomicResearchComparableSummary => record != null),
    comparableAggregates: research.comparableAggregates?.filter(aggregate => facets.has(aggregate.topic)),
    basisCounts: jobsContext ? research.basisCounts : {},
    fiscalBaselineDrafts: jobsContext ? research.fiscalBaselineDrafts : [],
  } : undefined;
  const hasSelectedFact = Boolean(
    (jobsContext && (
      (data.workforceProfile?.observations.length ?? 0) > 0
      || hasJobsOrFiscalScreeningFact(data.screening)
    ))
    || (waterContext
      && data.waterUseScreening?.status === 'calculated'
      && data.waterUseScreening.scenarios.length > 0)
    || (projectedResearch?.policyValidRecords.length ?? 0) > 0
    || projectedResearch?.comparableAggregates?.some(aggregate => (
      aggregate.numeric.length > 0 || aggregate.categorical.length > 0
    )),
  );
  // `economicEvidence` is additive. Once report-facet filtering removes every
  // usable fact, omit the section rather than preserving a verified shell that
  // can inflate completeness or emit an identity-only provider reference.
  if (!hasSelectedFact) return undefined;
  // Rebuild contributions from the refs that survived projection. Filtering
  // the original contributions by URL is insufficient because one filing can
  // supply distinct water and power facts at different excerpts/locators.
  const projectedSourceContributions = [
    ...workforceSourceContributions(
      jobsContext ? data.workforceProfile : undefined,
      section.retrievedAt,
    ),
    ...economicSourceContributions(jobsContext ? data.screeningSources : [], {
      path: 'screening',
      grade: 'inference',
      qualification: 'estimated',
      retrievedAt: data.screening?.generatedAt ?? section.retrievedAt,
    }),
    ...economicSourceContributions(waterContext ? (data.waterScreeningSources ?? []) : [], {
      path: 'waterUseScreening',
      grade: 'inference',
      qualification: 'estimated',
      retrievedAt: data.screening?.generatedAt ?? section.retrievedAt,
    }),
    ...economicSourceContributions(researchSources(projectedResearch), {
      path: 'research.policyValidRecords',
      grade: 'regional-screening',
      qualification: 'screening',
      retrievedAt: projectedResearch?.generatedAt ?? section.retrievedAt,
    }),
  ];
  return {
    ...section,
    sourceContributions: projectedSourceContributions,
    data: {
      ...data,
      ...(jobsContext && data.workforceProfile ? { workforceProfile: data.workforceProfile } : { workforceProfile: undefined }),
      ...(jobsContext && data.screening ? { screening: data.screening } : { screening: undefined }),
      screeningSources: jobsContext ? data.screeningSources : [],
      ...(waterContext && data.waterUseScreening ? { waterUseScreening: data.waterUseScreening } : { waterUseScreening: undefined }),
      waterUseScenarioScreens: waterContext ? data.waterUseScenarioScreens : undefined,
      waterScreeningSources: waterContext ? (data.waterScreeningSources ?? []) : [],
      ...(projectedResearch ? { research: projectedResearch } : { research: undefined }),
    },
  };
}

function rangeText(range: { low: number; high: number } | null | undefined): string {
  if (!range) return 'not calculated';
  return range.low === range.high ? String(range.low) : `${range.low}-${range.high}`;
}

function durationText(duration: number | { low: number; high: number } | null | undefined): string {
  if (duration == null) return 'duration not calculated';
  return typeof duration === 'number'
    ? `${duration} months`
    : `${rangeText(duration)} months`;
}

function sourceLines(sources: readonly EconomicSourceRef[], limit = 8): string[] {
  const unique = [...new Map(sources.map(source => [`${source.id}|${source.url}`, source])).values()];
  // P3.6 — was 3; `sourceRefIds` typically holds 5–7 and a citation the model
  // cannot see is a citation it cannot make.
  return unique.slice(0, limit).map(source => (
    `      Source: ${source.publisher}, "${source.excerpt.slice(0, 240)}" `
    + `(${source.locator}) <${source.url}>`
  ));
}

/**
 * Delegates to the shared band formatter. The previous local copy chose the unit
 * per VALUE, so a band straddling 1,000,000 gal/day printed its two ends in
 * different units — `201,487 gal/day-1.83 MGD` for Pineywoods' operator-fleet
 * row, against wet-tower rows quoted in MGD. See src/lib/waterRateFormat.ts.
 *
 * P3.6 still holds: every band printed here (daily, annual, hourly) is in a form
 * the WATER-TRUTH-1 guard's `endpointMatches` admits as the exact stored
 * endpoint, so a verbatim restatement passes and a shifted one blocks.
 */
const waterRateText = formatWaterRateBand;

/**
 * Who reads a rendered water screen. `prompt` is the SED text a report model
 * reads; `report` is the saved "Modeled Water Use" section (reader, PDF, DOCX,
 * PPTX). Both come from ONE builder with identical numbers; `prompt` only adds
 * the model instruction and the verbatim source excerpts.
 */
export type WaterScreenTextAudience = 'prompt' | 'report';

/** One formatted figure on a scenario row, exactly as the saved text prints it. */
export interface WaterScreenFigure {
  /** Reader label for the measure, e.g. `average day`. */
  measure: string;
  /** Formatted band, e.g. `0.19–1.72 MGD`. */
  value: string;
}

/** A conditional quantity and the condition it holds under; the saved text prints `value qualifier`. */
export interface WaterScreenConditional {
  /** `potentially 0 gal/day`. */
  value: string;
  /** `only in verified full-capacity dry mode; dry capacity is unverified`. */
  qualifier: string;
}

/** The hourly adiabatic-assist estimate on a dry row, formatted. */
export interface WaterScreenAssist {
  label: string;
  /** The row qualifier, e.g. `modeled screening from hourly weather`. */
  basis: string;
  /** Annual-average daily makeup band. */
  averageDay: string;
  annual: string;
  activeHours: string;
  peakHour: string;
}

/** One screening scenario, formatted. */
export interface WaterScreenRow {
  scenarioId: string;
  label: string;
  applicability: string;
  boundary: string;
  flowBasis: string;
  figures: WaterScreenFigure[];
  /** Replaces `figures` when the quantity is conditional (unverified dry capacity). */
  conditional?: WaterScreenConditional;
  /** Printed directly under the row: the adiabatic makeup on the dry path. */
  assist?: WaterScreenAssist;
  /** Supporting sub-results after the assist: hourly weather basis, dry-cooling temperature screen. */
  details: string[];
}

/** One cited source, grouped by URL. */
export interface WaterScreenSource {
  /** Publisher, title and locator(s); no URL. */
  citation: string;
  url: string;
  /** Verbatim stored excerpts. Prompt-only. */
  excerpts: Array<{ locator: string; excerpt: string }>;
}

/**
 * The formatted water screen: every string a surface prints. The saved text
 * (and, for newly generated reports, the reader table) is a serialization of
 * this object, so no surface formats a number of its own.
 */
export interface WaterUseScreeningPresentation {
  header: string;
  /** Recorded MW scope and basis, exactly as the header prints it. */
  scope: string;
  catalogVersion: string;
  lead: string;
  basis: string[];
  rows: WaterScreenRow[];
  caveats: string[];
  notes: string[];
  /** Model-only instructions; never saved into a report. */
  promptInstructions: string[];
  sources: WaterScreenSource[];
}

/**
 * Scenario assumptions whose content the `Modeled basis` lines already state
 * from structured data (with human labels and collapsed ranges). Printing the
 * raw strings as well repeated each value in a second, unformatted form
 * (`PUE range: 1.18-1.18 (site-record).`).
 */
const ASSUMPTION_SHOWN_IN_BASIS: readonly RegExp[] = [
  /^Recorded load range: /,
  /^PUE range: /,
  /^Annual utilization range: /,
  /^Cooling-tower cycles of concentration: /,
  /^Recorded operating model: /,
  /^Recorded workload: /,
  /^Recorded IT cooling loop: /,
  /^Local climate from /,
  // Stated with its consequence by the EPA/ASHRAE conversion limitation.
  /^Nominal cooling-tower ton basis: /,
];

/**
 * Exact model/catalog strings that restate a key caveat, the row qualifier, or
 * the lead's climate sentence. Only exact matches are dropped, so any string a
 * future model version adds is printed rather than silently lost; the
 * `__tests__/waterScreenPresentation.test.ts` sweep pins that every entry is
 * still produced by the current model.
 */
const RESTATED_BY_KEY_CAVEAT: ReadonlySet<string> = new Set([
  // Key caveat: modeled screening, not metered/reported/requested/permitted/design.
  'All values are deterministic early-stage screening ranges; none is metered, reported, requested, permitted, or a design commitment for the subject site.',
  'This is a modeled screening range, not metered, reported, permitted, requested, or design water use for the subject site.',
  // Key caveat: averages are not peak-day; 100% load is a cooling sensitivity.
  'Annual-average ranges are distinct from the 100%-load cooling sensitivity and must not be described as peak-day demand.',
  'Average annual and 100%-load sensitivities are not peak-day, permitted, requested, design, or metered subject-site quantities.',
  // Key caveat: whole-site rows are alternatives to, not sums with, cooling-process rows.
  'The site-WUE numerator represents total onsite water use and must not be added to a cooling-tower row.',
  // Key caveat: zero dry-mode water is conditional and not a total-site claim.
  'Zero applies only to modeled evaporative cooling-process water, not total site water.',
  'Zero is a conditional dry-operation endpoint, not proof that the selected equipment can reject the full site heat load at the local design dry-bulb temperature.',
  'The modeled cold-end heat rejection uses no evaporative cooling water.',
  // Row qualifier ("modeled technology sensitivity") and key caveat on sensitivities.
  'The subject heat-rejection design is not a confirmed wet-only tower; this row is a technology sensitivity, not the selected design.',
  'The subject heat-rejection design is not confirmed dry-only; this row is a technology sensitivity.',
  'Heat rejection is unknown, so wet and dry rows are separate technology sensitivities rather than one blended prediction.',
  // Stated once by the wet-tower limitation that follows it in the notes.
  'All modeled facility electrical input is treated as heat rejected at the cooling-tower boundary.',
  // Key caveat on the hourly adiabatic estimate.
  'A site-coordinate-selected, nearest-grid 8,760-hour TMY drives the separate adiabatic-assist water range; generic airflow, pad effectiveness, activation thresholds, and cycles of concentration remain disclosed screening assumptions.',
  // The lead's climate sentence.
  'Trusted exact-current site climate was unavailable, so this quantity uses the cited industry benchmark without a local ambient adjustment.',
  'No trusted exact-current local climate was available for this run.',
  'The local SED climate is disclosed for design context; the quantity is not numerically climate-adjusted without hourly weather, set points, equipment curves, and control schedules.',
  'Local climate qualifies cooling-system suitability, but no hourly economizer or hybrid wet-mode schedule is fabricated from annual/monthly summaries.',
  'Exact-current local dry-bulb, wet-bulb, and humidity conditions are carried into the result for suitability review; they do not become a false-precision annual operating schedule.',
]);

/** @internal Exported for the dedupe sweep test only. */
export const WATER_SCREEN_RESTATED_TEXT = {
  assumptionPatterns: ASSUMPTION_SHOWN_IN_BASIS,
  restatedByKeyCaveat: RESTATED_BY_KEY_CAVEAT,
} as const;

const WATER_DUTY_BOUNDARY_INSTRUCTION = 'Where the report carries a promoted generation scenario water DUTY, that duty is modeled at the equipment/process boundary and is a different quantity from these site-boundary bands: do not compare, convert, sum, or reconcile the two, and never present one as the other.';

function isDifferentRange(range: { low: number; high: number } | undefined | null): boolean {
  return range != null && range.low !== range.high;
}

function isDryConditional(scenario: WaterUseScenarioResult): boolean {
  return scenario.kind === 'dry-cooling' && scenario.designQualification === 'dry-capacity-unverified';
}

function cyclesRangeText(range: { low: number; high: number }): string {
  const format = (value: number): string => value.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return format(range.low) === format(range.high)
    ? format(range.low)
    : `${format(range.low)}–${format(range.high)}`;
}

/**
 * Deterministic lead: how many scenarios there are, that they are
 * alternatives rather than parts of a total, and what drives the differences
 * between and within rows. It carries no water quantity and no MW figure, so
 * it can never be read as a band attached to a load.
 *
 * Every sentence must also stay clear of the R-131 absence-assertion detector
 * (`absenceAssertionDetector.ts`, blocking tier): the judge sees only the
 * section's citations, which never record the site's heat rejection, so an
 * absence framing such as "is not yet recorded" is held. "Heat rejection is
 * unknown" is the model's own framing and is exempt.
 */
function waterScreenLead(water: WaterUseScreeningResult): string {
  const rows = water.scenarios;
  if (rows.length === 0) return 'No modeled screening scenario was calculated for this site.';
  const context = water.resolvedContext;
  const sentences: string[] = [];
  sentences.push(rows.length === 1
    ? 'One modeled screening scenario is shown; it is a screening range, not a site quantity.'
    : `${capitalizeFirst(countWord(rows.length))} modeled screening scenarios are shown. They are alternatives to compare, not amounts to add together.`);

  const wholeSite = rows.filter(row => row.boundary === 'onsite-direct-water').length;
  const coolingProcess = rows.length - wholeSite;
  const technologies = [...new Set(rows
    .map(row => WATER_SCENARIO_TECHNOLOGY_LABELS[row.kind])
    .filter((label): label is string => label != null))];
  if (wholeSite > 0 && coolingProcess > 0) {
    sentences.push(
      `${capitalizeFirst(countWord(wholeSite))} ${wholeSite === 1 ? 'is a published whole-site benchmark' : 'are published whole-site benchmarks'}`
      + ` and ${countWord(coolingProcess)} ${coolingProcess === 1 ? 'models' : 'each model'} cooling-process water for one heat-rejection technology`
      + `${technologies.length > 0 ? ` (${technologies.join(' or ')})` : ''}.`,
    );
  }
  // Only what actually differs between these rows. Withdrawal vs consumption
  // is named whenever both appear: two wet-tower rows share technology and
  // boundary and differ only in that flow basis.
  const flowBases = new Set(rows.map(row => row.flowBasis));
  const benchmarkRows = rows.filter(row => row.applicability === 'benchmark').length;
  const betweenRows = [
    wholeSite > 0 && coolingProcess > 0
      ? `the boundary each row measures (${WATER_BOUNDARY_LABELS['onsite-direct-water']} or ${WATER_BOUNDARY_LABELS['cooling-process-only']})`
      : null,
    technologies.length > 1 ? 'the heat-rejection technology' : null,
    flowBases.has('withdrawal') && flowBases.has('consumption')
      ? 'whether a row counts withdrawal or consumption'
      : flowBases.size > 1 ? 'the flow basis each row counts' : null,
    benchmarkRows > 1 ? 'the published source behind each benchmark' : null,
  ].filter((part): part is string => part != null);
  if (rows.length > 1 && betweenRows.length > 0) {
    sentences.push(`The differences between rows mainly reflect ${joinWithAnd(betweenRows)}.`);
  }

  const heatRejection = context?.heatRejection ?? 'unknown';
  if (heatRejection === 'unknown') {
    sentences.push('Heat rejection is unknown, so no row is the selected design.');
  } else if (rows.some(row => row.applicability === 'selected-technology')) {
    sentences.push(`The recorded heat-rejection technology is ${HEAT_REJECTION_LABELS[heatRejection]}; rows for other technologies are sensitivities.`);
  } else {
    sentences.push(`The recorded heat-rejection technology (${HEAT_REJECTION_LABELS[heatRejection]}) has no single matching row, so the technology rows are sensitivities.`);
  }

  // Within-row spread, attributed to the input that produces it. A wet-tower
  // withdrawal row's coefficient spread is the cycles-of-concentration range
  // (recorded, or the EPA 3–5 screening assumption the catalog's makeup range
  // is derived from), not a published benchmark range.
  const spreads = (row: WaterUseScenarioResult): boolean => row.coefficient.low !== row.coefficient.high;
  const cycles = context?.cyclesOfConcentration;
  const cyclesDriveWithdrawal = (row: WaterUseScenarioResult): boolean => (
    row.kind === 'wet-cooling-tower-withdrawal' && cycles != null && isDifferentRange(cycles)
  );
  const withinRow = [
    isDifferentRange(water.loadRange) ? 'the recorded load range' : null,
    isDifferentRange(context?.pue) ? 'the PUE range' : null,
    isDifferentRange(context?.utilization) ? 'the annual utilization range' : null,
    rows.some(row => spreads(row) && row.applicability === 'benchmark') ? 'the published benchmark ranges' : null,
    cycles && rows.some(row => spreads(row) && cyclesDriveWithdrawal(row))
      ? `the cooling-tower cycles-of-concentration range (${SCREENING_INPUT_SOURCE_LABELS[cycles.source]})`
      : null,
    rows.some(row => spreads(row) && row.applicability !== 'benchmark' && !cyclesDriveWithdrawal(row))
      ? 'the published cooling-water coefficient ranges'
      : null,
  ].filter((part): part is string => part != null);
  if (withinRow.length > 0) {
    sentences.push(`Within each row, the low-to-high spread comes from ${joinWithAnd(withinRow)}.`);
  }

  const hourlyAdiabatic = rows.some(row => row.adiabaticWaterScreening?.status === 'calculated');
  const localClimate = context?.ambientConditions != null;
  sentences.push(hourlyAdiabatic
    ? localClimate
      ? 'Hourly weather at the site coordinates sets only the adiabatic-assist estimate; local climate is design context for the other rows and does not adjust them.'
      : 'Hourly weather selected at the site coordinates sets only the adiabatic-assist estimate; separate ambient-design climate inputs were unavailable, so the other rows use disclosed benchmark and load assumptions without an ambient adjustment.'
    : localClimate
      ? 'Local climate is shown as design context only; without hourly weather it does not adjust any row.'
      : 'No trusted local climate record was available, so no row is adjusted for local climate.');
  return sentences.join(' ');
}

function waterScreenBasis(water: WaterUseScreeningResult): string[] {
  const context = water.resolvedContext;
  if (!context) return [];
  const lines: string[] = [];
  lines.push(
    `Modeled basis: ${formatPueRange(context.pue)} (${SCREENING_INPUT_SOURCE_LABELS[context.pue.source]}); `
    + `annual utilization ${formatFractionPercentRange(context.utilization)} (${SCREENING_INPUT_SOURCE_LABELS[context.utilization.source]}); `
    + `heat rejection ${HEAT_REJECTION_LABELS[context.heatRejection]}; `
    + `IT load ${formatDerivedMwRange(context.itLoadMw, water.loadRange)}; facility heat ${formatDerivedMwRange(context.facilityHeatMw, water.loadRange)}`
    + `${context.cyclesOfConcentration
      ? `; cooling-tower cycles of concentration ${cyclesRangeText(context.cyclesOfConcentration)} (${SCREENING_INPUT_SOURCE_LABELS[context.cyclesOfConcentration.source]})`
      : ''}.`,
  );
  const dataCenter = [
    context.operatingModel ? `operating model ${DATA_CENTER_OPERATING_MODEL_LABELS[context.operatingModel]}` : null,
    context.workload ? `workload ${DATA_CENTER_WORKLOAD_LABELS[context.workload]}` : null,
    context.rackCoolingStrategy ? `IT cooling loop ${IT_COOLING_LOOP_LABELS[context.rackCoolingStrategy]}` : null,
  ].filter((part): part is string => part != null);
  if (dataCenter.length > 0) lines.push(`Modeled data-center context: ${dataCenter.join('; ')}.`);
  const ambient = context.ambientConditions;
  if (ambient) {
    lines.push(
      `Screening climate context: ${formatCelsius(ambient.averageDryBulbC)} average and ${formatCelsius(ambient.peakDryBulbC)} SED peak dry bulb; `
      + `${formatCelsius(ambient.averageWetBulbC)} average and ${formatCelsius(ambient.designWetBulbC)} ${WET_BULB_BASIS_LABELS[ambient.wetBulbBasis]} wet bulb; `
      + `${formatWholeNumber(ambient.relativeHumidityPct)}% average relative humidity (${ambient.sourceLabel}).`,
    );
  }
  return lines;
}

function dryCoolingScreenDetails(
  scenario: WaterUseScenarioResult,
  recordedLoad: WaterUseScreeningResult['loadRange'],
): string[] {
  const screen = scenario.dryCoolingDesignScreen;
  if (!screen) return [];
  const classResults = (status: 'dryStatus' | 'adiabaticStatus'): string => screen.classChecks
    .map(check => `${check.waterClass} ${DRY_COOLING_CAPACITY_STATUS_LABELS[check[status]]}`)
    .join('; ');
  return [
    `Dry-cooling temperature screen (published design ranges): ${formatCelsius(screen.sitePeakDryBulbProxyC)} SED peak dry-bulb proxy; `
    + `${formatCelsius(screen.siteWetBulbC)} ${WET_BULB_BASIS_LABELS[screen.wetBulbBasis]} wet bulb; `
    + `full declared facility heat ${formatDerivedMwRange(screen.screeningDutyMw, recordedLoad)}; `
    + `dry-cooler approach ${formatCelsiusRange(screen.dryCoolerApproachC)}; `
    + `CDU and additional plate-heat-exchanger allowance ${formatCelsiusRange(screen.seriesHeatExchangerApproachC)}; `
    + `total ambient-to-facility-supply approach ${formatCelsiusRange(screen.totalAmbientToFacilitySupplyApproachC)}.`,
    `Dry-only result by facility-water class: ${classResults('dryStatus')}.`,
    `With adiabatic pre-cooling (${formatFractionPercentRange(screen.adiabaticSaturationEffectiveness)} effectiveness; `
    + `coil-entering air ${formatCelsiusRange(screen.adiabaticCoilInletC)}): ${classResults('adiabaticStatus')}.`,
  ];
}

function waterScreenAssist(scenario: WaterUseScenarioResult): WaterScreenAssist | undefined {
  const adiabatic = scenario.adiabaticWaterScreening;
  if (adiabatic?.status !== 'calculated') return undefined;
  return {
    label: 'Adiabatic assist on the dry path',
    basis: 'modeled screening from hourly weather',
    averageDay: waterRateText(adiabatic.averageDailyMakeupGallons),
    annual: formatAnnualWaterBand(adiabatic.annualMakeupGallons),
    activeHours: formatWholeRange(adiabatic.activeHours, 'active hours/year'),
    peakHour: formatHourlyWaterBand(adiabatic.peakActiveHourMakeupGallonsPerHour),
  };
}

/** `Adiabatic assist on the dry path (modeled screening from hourly weather): …` — the saved line. */
export function waterScreenAssistText(assist: WaterScreenAssist): string {
  return `${assist.label} (${assist.basis}): `
    + `annual-average makeup ${assist.averageDay}; `
    + `annual makeup ${assist.annual}; `
    + `${assist.activeHours}; `
    + `peak active-hour makeup ${assist.peakHour}.`;
}

function hourlyWeatherBasis(scenario: WaterUseScenarioResult): string[] {
  const adiabatic = scenario.adiabaticWaterScreening;
  if (adiabatic?.status !== 'calculated') return [];
  const tmy = adiabatic.provenance.tmy;
  const datasetIdentity = [tmy.datasetName, tmy.sourceVersion]
    .filter((value): value is string => Boolean(value))
    .join(' / ');
  const datasetPeriod = tmy.datasetYear == null
    ? 'multi-year composite typical year; no single dataset year declared'
    : `provider-declared dataset year ${String(tmy.datasetYear)}`;
  const gridIdentity = tmy.gridLatitude != null && tmy.gridLongitude != null
    ? `nearest source-grid coordinates ${String(tmy.gridLatitude)}, ${String(tmy.gridLongitude)}`
      + `${tmy.locationId ? `; location ID ${tmy.locationId}` : ''}`
    : 'the provider did not return a distinct grid coordinate';
  return [
    `Hourly weather basis: ${TMY_SOURCE_LABELS[tmy.source]}${datasetIdentity ? ` (${datasetIdentity})` : ''}; `
    + `${formatWholeNumber(tmy.hourCount)} hourly records; ${datasetPeriod}; `
    + `requested site coordinates ${tmy.latitudeRounded}, ${tmy.longitudeRounded}; ${gridIdentity}.`,
  ];
}

/** Reader names of a row's figures; the saved text prints `measure value`. */
const FIGURE_MEASURE = {
  averageDay: 'average day',
  annual: 'annual',
  fullLoad: '100%-load cooling sensitivity',
} as const;

function waterScreenRow(
  scenario: WaterUseScenarioResult,
  recordedLoad: WaterUseScreeningResult['loadRange'],
): WaterScreenRow {
  const assist = waterScreenAssist(scenario);
  const base = {
    scenarioId: scenario.scenarioId,
    label: scenario.label,
    applicability: WATER_APPLICABILITY_LABELS[scenario.applicability],
    boundary: WATER_BOUNDARY_LABELS[scenario.boundary],
    flowBasis: WATER_FLOW_BASIS_LABELS[scenario.flowBasis],
    ...(assist ? { assist } : {}),
    // The weather basis follows the assist it drives; the temperature screen follows both.
    details: [...hourlyWeatherBasis(scenario), ...dryCoolingScreenDetails(scenario, recordedLoad)],
  };
  if (isDryConditional(scenario)) {
    return {
      ...base,
      figures: [],
      conditional: {
        value: `potentially ${waterRateText(scenario.averageGallonsPerDay)}`,
        qualifier: 'only in verified full-capacity dry mode; dry capacity is unverified',
      },
    };
  }
  return {
    ...base,
    figures: [
      { measure: FIGURE_MEASURE.averageDay, value: waterRateText(scenario.averageGallonsPerDay) },
      { measure: FIGURE_MEASURE.annual, value: formatAnnualWaterBand(scenario.annualGallons) },
      ...(scenario.fullLoadGallonsPerDay
        ? [{ measure: FIGURE_MEASURE.fullLoad, value: waterRateText(scenario.fullLoadGallonsPerDay) }]
        : []),
    ],
  };
}

function waterScreenCaveats(water: WaterUseScreeningResult): string[] {
  const rows = water.scenarios;
  if (rows.length === 0) return [];
  const wholeSite = rows.some(row => row.boundary === 'onsite-direct-water');
  const coolingProcess = rows.some(row => row.boundary === 'cooling-process-only');
  const benchmark = rows.some(row => row.applicability === 'benchmark');
  const sensitivity = rows.some(row => row.applicability === 'sensitivity');
  const fullLoad = rows.some(row => row.fullLoadGallonsPerDay != null && !isDryConditional(row));
  const dry = rows.some(row => row.kind === 'dry-cooling');
  const hourlyAdiabatic = rows.some(row => row.adiabaticWaterScreening?.status === 'calculated');
  return [
    'These are modeled screening bands, not permitted demand, a design guarantee, or measured site use; no row is a metered, reported, requested, or permitted quantity for the site.',
    wholeSite && coolingProcess
      ? 'Total-site WUE rows include onsite water and are alternatives to, not sums with, cooling-process rows.'
      : 'The rows are alternatives, not amounts to add together.',
    ...(benchmark && sensitivity
      ? ['A published-benchmark row scales fleet or national averages to this load; a technology-sensitivity row shows one heat-rejection technology’s cooling-process water and is not the selected design.']
      : []),
    `Average-day and annual figures are annual averages, not peak-day demand${fullLoad ? '; 100%-load figures are a cooling sensitivity, not a design-day quantity' : ''}.`,
    ...(rows.some(isDryConditional)
      ? ['Potentially zero evaporative process water applies only while verified equipment carries the full duty in dry mode; it is not a total-site water claim.']
      : []),
    ...(dry
      ? [hourlyAdiabatic
        ? 'Adiabatic assist water uses site-coordinate-selected, nearest-grid hourly weather plus disclosed generic airflow, effectiveness, activation, and COC assumptions; it is a screening estimate, not an OEM selection or control guarantee.'
        : 'Adiabatic assist water is not estimated: it needs site-coordinate-selected hourly weather, and monthly climate is not substituted for an hourly schedule.']
      : []),
  ];
}

function waterScreenNotes(water: WaterUseScreeningResult): string[] {
  const assumptions = water.scenarios.flatMap(scenario => [...scenario.assumptions]);
  const limitations = [
    ...water.limitations,
    ...water.scenarios.flatMap(scenario => [...scenario.limitations]),
  ];
  return [...new Set([...assumptions, ...limitations])].filter(text => (
    !RESTATED_BY_KEY_CAVEAT.has(text)
    && !ASSUMPTION_SHOWN_IN_BASIS.some(pattern => pattern.test(text))
  ));
}

/**
 * The frozen water sources that a scenario row actually cites
 * (`scenario.sourceRefs`, matched on id and URL). Both the printed source list
 * and the report references added by `applyWaterScreeningDisclosure` use this
 * one filter, so the section never lists a source no row relies on.
 */
export function citedWaterScreeningSources(
  data: Pick<EconomicEvidenceData, 'waterUseScreening' | 'waterScreeningSources'>,
): EconomicSourceRef[] {
  const scenarios = data.waterUseScreening?.scenarios ?? [];
  const cited = new Set(scenarios.flatMap(scenario => scenario.sourceRefs.map(ref => `${ref.id}|${ref.url}`)));
  const sources = data.waterScreeningSources ?? scenarios.flatMap(scenario => [...scenario.sourceRefs]);
  const unique = new Map<string, EconomicSourceRef>();
  for (const source of sources) {
    const key = `${source.id}|${source.url}`;
    if (cited.has(key) && !unique.has(key)) unique.set(key, source);
  }
  return [...unique.values()];
}

function waterScreenSources(
  data: Pick<EconomicEvidenceData, 'waterUseScreening' | 'waterScreeningSources'>,
): WaterScreenSource[] {
  const byUrl = new Map<string, { publisher: string; title: string; locators: string[]; excerpts: WaterScreenSource['excerpts'] }>();
  const add = (source: { publisher: string; title: string; url: string; locator: string; excerpt?: string }): void => {
    const entry = byUrl.get(source.url) ?? { publisher: source.publisher, title: source.title, locators: [], excerpts: [] };
    if (!entry.locators.includes(source.locator)) entry.locators.push(source.locator);
    if (source.excerpt) entry.excerpts.push({ locator: source.locator, excerpt: source.excerpt.slice(0, 240) });
    byUrl.set(source.url, entry);
  };
  for (const source of citedWaterScreeningSources(data)) add(source);
  for (const scenario of data.waterUseScreening?.scenarios ?? []) {
    if (scenario.adiabaticWaterScreening?.status !== 'calculated') continue;
    for (const source of scenario.adiabaticWaterScreening.provenance.sources) add(source);
  }
  return [...byUrl.entries()].map(([url, entry]) => ({
    citation: `${entry.publisher}, ${entry.title} (${entry.locators.join('; ')})`,
    url,
    excerpts: entry.excerpts,
  }));
}

/** Format the frozen screen once; every surface serializes this object. */
export function buildWaterUseScreeningPresentation(
  data: Pick<EconomicEvidenceData, 'waterUseScreening' | 'waterScreeningSources'>,
): WaterUseScreeningPresentation | null {
  const water = data.waterUseScreening;
  if (!water) return null;
  const load = water.loadRange;
  const loadQualifiers = [
    load.basisTreatment !== 'as-recorded' ? POWER_MW_BASIS_TREATMENT_LABELS[load.basisTreatment] : null,
    load.basisSource === 'screening-assumption' ? `${POWER_MW_BASIS_LABELS[load.basis]} basis assumed for screening` : null,
  ].filter((part): part is string => part != null);
  const scope = `${formatRecordedMwScope(load)} ${POWER_MW_BASIS_LABELS[load.basis]}`
    + `${loadQualifiers.length > 0 ? ` (${loadQualifiers.join('; ')})` : ''}`;
  return {
    header: `WATER-USE SCREEN: ${scope}; modeled screening estimates; benchmark catalog ${water.sourceCatalogVersion}.`,
    scope,
    catalogVersion: water.sourceCatalogVersion,
    lead: waterScreenLead(water),
    basis: waterScreenBasis(water),
    rows: water.scenarios.map(scenario => waterScreenRow(scenario, water.loadRange)),
    caveats: waterScreenCaveats(water),
    notes: waterScreenNotes(water),
    promptInstructions: [WATER_DUTY_BOUNDARY_INSTRUCTION],
    sources: waterScreenSources(data),
  };
}

/**
 * The reader projection of the saved section (`SiteRagReport.waterScreeningView`):
 * the same formatted strings the saved text prints, arranged for a table. It
 * carries no prompt-only content and no citation tokens (the reader resolves a
 * source's report reference by URL, so reference renumbering cannot strand it).
 */
export function buildWaterScreeningView(
  data: Pick<EconomicEvidenceData, 'waterUseScreening' | 'waterScreeningSources'>,
): WaterScreeningView | null {
  const presentation = buildWaterUseScreeningPresentation(data);
  if (!presentation) return null;
  const figure = (row: WaterScreenRow, measure: string): string | null => (
    row.figures.find(candidate => candidate.measure === measure)?.value ?? null
  );
  return {
    schemaVersion: WATER_SCREENING_VIEW_SCHEMA_VERSION,
    scope: presentation.scope,
    catalogVersion: presentation.catalogVersion,
    lead: presentation.lead,
    basis: presentation.basis,
    rows: presentation.rows.map(row => ({
      scenarioId: row.scenarioId,
      label: row.label,
      applicability: row.applicability,
      boundary: row.boundary,
      flowBasis: row.flowBasis,
      averageDay: figure(row, FIGURE_MEASURE.averageDay),
      annual: figure(row, FIGURE_MEASURE.annual),
      fullLoad: figure(row, FIGURE_MEASURE.fullLoad),
      conditional: row.conditional ? { ...row.conditional } : null,
      assist: row.assist ? { ...row.assist } : null,
      details: row.details,
    })),
    caveats: presentation.caveats,
    notes: presentation.notes,
    sources: presentation.sources.map(source => ({ citation: source.citation, url: source.url })),
  };
}

/** `  - label (applicability; boundary; flow): figures.` — one line per scenario. */
export function waterScreenRowText(row: WaterScreenRow): string {
  const body = row.conditional
    ? `${row.conditional.value} ${row.conditional.qualifier}`
    : row.figures.map(figure => `${figure.measure} ${figure.value}`).join('; ');
  return `${row.label} (${row.applicability}; ${row.boundary}; ${row.flowBasis}): ${body}.`;
}

/** The same frozen scenario values and qualifications feed prompts and the
 * saved reader/export section. No second calculation or model paraphrase.
 * The `report` text is the `prompt` text minus the model instruction and the
 * verbatim source excerpts; every number is identical in both. */
export function renderWaterUseScreeningText(
  data: Pick<EconomicEvidenceData, 'waterUseScreening' | 'waterScreeningSources'>,
  audience: WaterScreenTextAudience,
  options: { compact?: boolean } = {},
): string {
  const presentation = buildWaterUseScreeningPresentation(data);
  if (!presentation) return '';
  const lines: string[] = [presentation.header, presentation.lead, ...presentation.basis];
  if (presentation.rows.length > 0) {
    lines.push('Modeled scenario bands:');
    for (const row of presentation.rows) {
      lines.push(`  - ${waterScreenRowText(row)}`);
      if (row.assist) lines.push(`    ${waterScreenAssistText(row.assist)}`);
      if (!options.compact) lines.push(...row.details.map(detail => `    ${detail}`));
    }
  }
  lines.push('Key caveats:', ...presentation.caveats.map(text => `  - ${text}`));
  if (audience === 'prompt') lines.push(...presentation.promptInstructions.map(text => `  - ${text}`));
  if (!options.compact && presentation.notes.length > 0) {
    lines.push('Other modeled assumptions and limitations:', ...presentation.notes.map(text => `  - ${text}`));
  }
  if (presentation.sources.length > 0) {
    lines.push('Sources:');
    for (const source of presentation.sources) {
      lines.push(`  - ${source.citation} <${source.url}>`);
      if (audience === 'prompt' && !options.compact) {
        lines.push(...source.excerpts.map(excerpt => `      Excerpt (${excerpt.locator}): "${excerpt.excerpt}"`));
      }
    }
  }
  return lines.join('\n');
}

/** Bounded, source-visible SED text consumed by report prompts. */
export function renderEconomicEvidenceForPrompt(data: EconomicEvidenceData): string {
  const lines: string[] = [
    'SCREENING EVIDENCE ONLY - not an operator commitment, hiring guarantee, permit entitlement, or tax/PILOT offer.',
    `Evidence revision: ${data.sourceRevision} | Content hash: ${data.contentHash}`,
  ];
  const waterText = renderWaterUseScreeningText(data, 'prompt', { compact: Boolean(data.waterUseScenarioScreens?.length) });
  // Keep the common definitions, caveats and primary bands ahead of optional
  // context, so a bounded projection cannot leave phase figures unqualified.
  if (waterText) lines.push(...waterText.split('\n'));
  if (data.waterUseScenarioScreens?.length) {
    lines.push('ADDITIONAL PHASE / MW-BOUNDARY SCREENING CASES: alternatives, not additive. Phase capacities are cumulative; never add their water or staffing totals. These data-center cases exclude power-plant process water and do not establish net generation or firm service.');
    for (const item of data.waterUseScenarioScreens) {
      const water = item.result;
      const ctx = water.resolvedContext;
      lines.push(`CASE ${item.scenarioId}: ${item.label}. ${item.assumption}`);
      if (ctx) lines.push(`  Modeled assumptions: ${formatPueRange(ctx.pue)} (${ctx.pue.source}); utilization ${formatFractionPercentRange(ctx.utilization)} (${ctx.utilization.source}); assumed IT ${formatDerivedMwRange(ctx.itLoadMw, water.loadRange)}; facility heat ${formatDerivedMwRange(ctx.facilityHeatMw, water.loadRange)}. These are conditional loads, not confirmed project facts.`);
      const presentation = buildWaterUseScreeningPresentation({ waterUseScreening: water, waterScreeningSources: data.waterScreeningSources });
      for (const row of presentation?.rows ?? []) {
        lines.push(`  - Modeled ${waterScreenRowText(row)}`);
        if (row.assist) lines.push(`    ${waterScreenAssistText(row.assist)}`);
      }
      lines.push('  Shared water-screen caveats and source definitions above apply to this case.');
    }
  }
  if (data.screening) {
    const screen = data.screening;
    lines.push(`ECONOMIC SCREEN: ${screen.snapshotId} (generated ${screen.generatedAt}; catalog ${screen.sourceCatalogVersion})`);
    lines.push(`  - Permanent onsite jobs: ${rangeText(screen.directJobs.range)} direct onsite FTE (${screen.directJobs.status}; ${screen.directJobs.method ?? 'method unavailable'})`);
    if (screen.directJobsMwRange) {
      lines.push(
        `  - Permanent onsite jobs across ${screen.directJobsMwRange.loadRange.low}-${screen.directJobsMwRange.loadRange.high} MW: `
        + `${rangeText(screen.directJobsMwRange.lowEndpoint.range)} at low endpoint; ${rangeText(screen.directJobsMwRange.highEndpoint.range)} at high endpoint`,
      );
    }
    if (screen.constructionJobs) {
      lines.push(`  - Construction peak onsite workers: ${rangeText(screen.constructionJobs.peakOnsiteWorkers)}; average: ${rangeText(screen.constructionJobs.averageOnsiteWorkers)}; direct job-years: ${rangeText(screen.constructionJobs.directJobYears)}; duration: ${durationText(screen.constructionJobs.durationMonths)}`);
    }
    if (screen.constructionJobsMwRange) {
      const range = screen.constructionJobsMwRange;
      const endpoint = (label: string, mw: number, result: typeof range.lowEndpoint): string => (
        `  - Construction at ${label} endpoint (${mw} MW): peak ${rangeText(result.peakOnsiteWorkers)} onsite workers; `
        + `average ${rangeText(result.averageOnsiteWorkers)}; direct job-years ${rangeText(result.directJobYears)}; `
        + `duration ${durationText(result.durationMonths)} (${result.status})`
      );
      lines.push(endpoint('low', range.loadRange.low, range.lowEndpoint));
      lines.push(endpoint('high', range.loadRange.high, range.highEndpoint));
    }
    const staffingLimitations = new Set(screen.directJobs.limitations ?? []);
    if (staffingLimitations.size) lines.push(`  Shared staffing limitations: ${[...staffingLimitations].join(' ')}`);
    for (const item of screen.additionalScenarios ?? []) {
      lines.push(`  CASE ${item.scenarioId}: ${item.label}. ${item.assumption}`);
      const jobsRange = item.directJobsMwRange;
      lines.push(jobsRange
        ? `    Modeled permanent onsite staffing: ${rangeText(jobsRange.lowEndpoint.range)} direct onsite FTE at the low load; ${rangeText(jobsRange.highEndpoint.range)} at the high load (${item.directJobs.status}).`
        : `    Modeled permanent onsite staffing: ${rangeText(item.directJobs.range)} direct onsite FTE (${item.directJobs.status}).`);
      const constructionRange = item.constructionJobsMwRange;
      if (constructionRange) lines.push(`    Modeled construction peak: ${rangeText(constructionRange.lowEndpoint.peakOnsiteWorkers)} onsite workers at the low load; ${rangeText(constructionRange.highEndpoint.peakOnsiteWorkers)} at the high load. Each case is a standalone cumulative-build sensitivity, not incremental phase hiring or a schedule.`);
      else if (item.constructionJobs) lines.push(`    Modeled construction peak: ${rangeText(item.constructionJobs.peakOnsiteWorkers)} onsite workers; duration ${durationText(item.constructionJobs.durationMonths)}. Each case is a standalone cumulative-build sensitivity, not incremental phase hiring or a schedule.`);
      const uniqueLimitations = (item.directJobs.limitations ?? []).filter(text => !staffingLimitations.has(text));
      uniqueLimitations.forEach(text => staffingLimitations.add(text));
      if (uniqueLimitations.length) lines.push(`    Additional staffing limitations: ${uniqueLimitations.join(' ')}`);
    }
    for (const fiscal of screen.fiscalScenarios) {
      lines.push(`  - USER-ENTERED fiscal scenario ${fiscal.scenarioId}: ${fiscal.jurisdictionLabel}, cumulative community revenue $${Math.round(fiscal.cumulativeCommunityRevenueUsd).toLocaleString('en-US')} (${fiscal.status})`);
    }
    lines.push(...sourceLines(data.screeningSources));
  }
  if (data.workforceProfile) {
    const profile = data.workforceProfile;
    lines.push(`WORKFORCE PROFILE: ${profile.countyName}, ${profile.stateCode} (county FIPS ${profile.countyFips}; snapshot ${profile.snapshotId})`);
    for (const observation of profile.observations) {
      const occupation = observation.occupation ? ` | ${observation.occupation.label}` : '';
      const industry = observation.industry ? ` | ${observation.industry.label}` : '';
      lines.push(
        `  - ${observation.metric}: ${observation.value} ${observation.units} | ${observation.geography.label}${occupation}${industry} | ${observation.period} | ${observation.sourceDataset} <${observation.sourceUrl}>`,
      );
    }
    if (profile.missingMetrics.length > 0) lines.push(`  Missing/suppressed metrics: ${profile.missingMetrics.join('; ')}`);
    if (profile.providerErrors.length > 0) lines.push(`  Provider errors: ${profile.providerErrors.join('; ')}`);
  }
  if (data.research) {
    const research = data.research;
    lines.push(
      `COMPARABLE PUBLIC-RECORD RESEARCH: run ${research.runId}; result ${research.resultSnapshotId}; policy ${research.policyVersion}; `
      + `${research.policyValidRecords.length} relevant policy-valid records.`,
    );
    lines.push(`  - ${research.nonPromoted.heldCount} held and ${research.nonPromoted.excludedCount} excluded candidates are NOT facts and are not listed below.`);
    for (const aggregate of research.comparableAggregates ?? []) {
      if (!aggregate.method) continue;
      for (const value of aggregate.numeric) {
        lines.push(
          `  - ${aggregate.topic.toUpperCase()} COHORT (${value.recordCount} compatible projects; ${aggregate.method}): `
          // P3.6 — no median: `quantile` interpolates, so for an even record count the
          // median is a value no record holds and the guard (per-record values only)
          // would block a faithful restatement of what this line printed.
          + `${value.low}-${value.high} ${value.units}; exact scope ${value.key}.`,
        );
      }
      for (const value of aggregate.categorical) {
        lines.push(
          `  - ${aggregate.topic.toUpperCase()} COHORT (${value.recordCount} compatible projects; ${aggregate.method}): `
          + `${value.key}.`,
        );
      }
    }
    const records = research.policyValidRecords.slice(0, 12);
    for (const record of records) {
      lines.push(`  - ${record.facilityName} (${record.locality ? `${record.locality}, ` : ''}${record.stateCode}) [project ${record.projectId}]`);
      if (record.mw) lines.push(`    Capacity: ${record.mw.value} MW ${record.mw.basis} (${record.mw.phaseBasis})`);
      if (record.permanentJobs) lines.push(`    Permanent onsite jobs: ${record.permanentJobs.low}-${record.permanentJobs.high} (${record.permanentJobs.evidenceBasis}; ${record.permanentJobs.phaseBasis})`);
      for (const observation of record.waterTimeline ?? []) {
        const value = observation.value;
        lines.push(`    Water ${value.stage}: ${value.amount} ${value.units} ${value.flow}; ${value.statistic}; ${value.boundary}${value.waterSource ? `; source ${value.waterSource}` : ''} (${observation.evidenceBasis}; ${observation.phaseBasis})`);
        lines.push(...sourceLines(observation.sources));
      }
      for (const observation of record.coolingObservations ?? []) {
        const value = observation.value;
        lines.push(`    Cooling (${value.scope}): rack=${value.rackCoolingStrategy ?? 'unknown'}; heat-rejection=${value.heatRejection ?? 'unknown'}; generation=${value.coolingGeneration ?? 'unknown'}${value.nativeDescription ? `; record wording=${value.nativeDescription}` : ''} (${observation.evidenceBasis})`);
        lines.push(...sourceLines(observation.sources));
      }
      for (const observation of record.powerObservations ?? []) {
        const value = observation.value;
        lines.push(`    Power: ${value.role}; ${value.status}; ${value.technologies.join(', ')}${value.capacityMw != null ? `; ${value.capacityMw} MW ${value.capacityBasis ?? 'basis unknown'}` : ''}${value.utility ? `; utility ${value.utility}` : ''} (${observation.evidenceBasis})`);
        lines.push(...sourceLines(observation.sources));
      }
      for (const observation of record.deliveryEvents ?? []) {
        const value = observation.value;
        lines.push(`    Delivery event ${value.occurredOn}: ${value.category}/${value.eventType}; effect=${value.effect}; ${value.summary} (${observation.evidenceBasis})`);
        lines.push(...sourceLines(observation.sources));
      }
      if (record.mw) lines.push(...sourceLines(record.mw.sources));
      if (record.permanentJobs) lines.push(...sourceLines(record.permanentJobs.sources));
    }
    if (research.policyValidRecords.length > records.length) {
      lines.push(`  - ${research.policyValidRecords.length - records.length} additional relevant records remain in the structured SED artifact; prompt list capped at 12.`);
    }
    if (research.sourceFailures.length > 0) {
      lines.push(`  Source coverage failures: ${research.sourceFailures.map(failure => `${failure.sourceClass} (${failure.failureKind ?? 'other'})`).join('; ')}`);
    }
  }
  // Report prompts need a hard, observable budget even when a research record
  // carries many observations/sources. The complete arrays remain in the SED
  // JSON; only this narrative projection is bounded.
  const maxRows = 260;
  const maxChars = data.waterUseScenarioScreens?.length ? 32_000 : 18_000;
  const bounded: string[] = [];
  let chars = 0;
  for (const line of lines) {
    if (bounded.length >= maxRows || chars + line.length + 1 > maxChars) break;
    bounded.push(line);
    chars += line.length + 1;
  }
  if (bounded.length < lines.length) {
    bounded.push(
      `[Economic evidence prompt projection truncated: ${lines.length - bounded.length} additional row(s) remain in the structured SED artifact.]`,
    );
  }
  return bounded.join('\n');
}
